/*
 * "Create on Bitrise": pure model behind the panel — which workspace and Bitrise account are picked, the provision
 * request bodies, what the steps and the heading show, and how the repository list is described. No React and nothing
 * that touches `window`.
 */
import type { BitriseProvisionInput } from '@/api/endpoints';
import { ExistingBitriseApp, type BitriseSetup, type BitriseSetupStep, type ProviderAccount, type ScmConnection, type ScmRepository, type ScmRepositoryList } from '@/api/schemas';

/** The code of the API failure whose steps already say what went wrong: no second copy of it in an alert. */
export const SETUP_FAILED_CODE = 'HGR-BITRISE-SETUP-FAILED';

export const STEP_LOOK: Record<BitriseSetupStep['status'], { icon: string; tone: string }> = {
  done: { icon: 'check_circle', tone: 'text-succeeded' },
  failed: { icon: 'error', tone: 'text-failed' },
  skipped: { icon: 'cancel', tone: 'text-subtle' },
  pending: { icon: 'pending', tone: 'text-subtle' },
};

/** The index of the step to show as running: the first one not done yet, while a request runs (-1 = none). */
export const activeStepIndex = (steps: BitriseSetupStep[], running: boolean | undefined): number => (running ? steps.findIndex((s) => s.status === 'pending' || s.status === 'failed') : -1);

/** A pending step reads as muted unless it is the running one. */
export const stepLabelMuted = (status: BitriseSetupStep['status'], isRunning: boolean): boolean => status === 'pending' && !isRunning;

/** A step's detail is shown once it started. */
export const showsStepDetail = (s: Pick<BitriseSetupStep, 'detail' | 'status'>): boolean => Boolean(s.detail) && s.status !== 'pending';

/* ---------- What is picked ---------- */

/** Connections whose token Bitbucket still accepts. */
export const usableConnections = (all: ScmConnection[] | undefined): ScmConnection[] => (all ?? []).filter((c) => c.status !== 'revoked');

/** Bitrise accounts that can take a build. */
export const bitriseAccountsOf = (all: ProviderAccount[] | undefined): ProviderAccount[] => (all ?? []).filter((a) => a.kind === 'bitrise' && a.status !== 'disabled');

/** The picked item, else the first one. */
export const pickById = <T extends { id: string }>(items: T[], id: string | undefined): T | undefined => items.find((x) => x.id === id) ?? items[0];

export const hasRevoked = (all: ScmConnection[] | undefined): boolean => (all ?? []).some((c) => c.status === 'revoked');

/* ---------- Provision requests ---------- */

/** The base of every provision request; nothing until a workspace and a repository are picked. */
export function provisionInput(connection: Pick<ScmConnection, 'id'> | undefined, repository: string, account: Pick<ProviderAccount, 'id'> | undefined): BitriseProvisionInput | null {
  if (!connection || !repository) return null;
  return { scmConnectionId: connection.id, repository, ...(account ? { providerAccountId: account.id } : {}) };
}

/** What to do when Bitrise already has an app for the repository: create another one, or reuse the one with this slug. */
export const withBitriseApp = (body: BitriseProvisionInput, choice: string): BitriseProvisionInput => ({
  ...body,
  bitriseApp: choice === 'create' ? { mode: 'create' } : { mode: 'reuse', slug: choice },
});

/** "Try again" resumes with what the failed setup recorded; null when it recorded no repository or workspace. */
export function retryInput(data: BitriseSetup | undefined): BitriseProvisionInput | null {
  const repository = data?.repository;
  const connectionId = data?.scmConnectionId;
  if (!repository || !connectionId) return null;
  return { scmConnectionId: connectionId, repository: repository.slug, ...(data?.providerAccountId ? { providerAccountId: data.providerAccountId } : {}) };
}

/** The 409 `existingApps` context, keeping only the entries that parse. */
export const parseExistingApps = (raw: unknown[]): ExistingBitriseApp[] => raw.map((a) => ExistingBitriseApp.safeParse(a)).flatMap((r) => (r.success ? [r.data] : []));

/** The radio selected when the list of existing apps opens: the first app, else "create another". */
export const defaultChoice = (apps: ExistingBitriseApp[]): string => apps[0]?.slug ?? 'create';

/* ---------- What the panel shows ---------- */

export type PanelBadge = 'done' | 'failed' | 'running' | 'idle';

/** The pill next to "Bitrise": on Bitrise, needs attention, creating, or nothing yet. */
export function panelBadge(done: boolean, data: BitriseSetup | undefined, running: boolean): PanelBadge {
  if (done) return 'done';
  if (data?.status === 'failed') return 'failed';
  return running ? 'running' : 'idle';
}

/** The setup is finished and linked to the app. */
export const isDone = (data: BitriseSetup | undefined): boolean => data?.status === 'done' && data.linked;

/** The explanation above the form is dropped in the compact (sheet) layout once the setup has started. */
export const showsIntro = (compact: boolean | undefined, data: BitriseSetup | undefined): boolean => !compact || !data || data.status === 'not_started';

/** The recorded steps to show: the setup's own once it started, an empty list while the first request runs. */
export function stepsToShow(data: BitriseSetup | undefined, running: boolean): { steps: BitriseSetupStep[]; running: boolean } | null {
  if (data && data.status !== 'not_started') return { steps: data.steps, running };
  return running ? { steps: data?.steps ?? [], running: true } : null;
}

/** After a failed setup (and nothing running) the panel offers "Try again". */
export const offersRetry = (data: BitriseSetup | undefined, running: boolean): boolean => data?.status === 'failed' && !running;

/** The prerequisites the person is missing, in the order the panel says them. */
export function missingPrerequisites(usable: ScmConnection[], bitriseAccounts: ProviderAccount[], revoked: boolean): string[] {
  return [
    ...(usable.length === 0 ? [`Connect a Bitbucket workspace first${revoked ? ' (its token was refused: replace it)' : ''}.`] : []),
    ...(bitriseAccounts.length === 0 ? ['Connect a Bitrise account first (workspace and token).'] : []),
  ];
}

/* ---------- Repository list ---------- */

/** "12+ in malible · filter to narrow the list": how many repositories came back and whether there are more. */
export const repoHint = (list: Pick<ScmRepositoryList, 'data' | 'more' | 'workspace'> | undefined): string | undefined =>
  list ? `${list.data.length}${list.more ? '+' : ''} in ${list.workspace}${list.more ? ' · filter to narrow the list' : ''}` : undefined;

export const repoError = (isError: boolean, error: unknown): string | undefined => {
  if (!isError) return undefined;
  return error instanceof Error ? error.message : 'Could not list the repositories';
};

/** "malible/storeai · main". */
export const repoLabel = (r: Pick<ScmRepository, 'fullName' | 'defaultBranch'>): string => `${r.fullName}${r.defaultBranch ? ` · ${r.defaultBranch}` : ''}`;

/** The filter box shows when the list is cut off or something is typed in it. */
export const showsRepoFilter = (list: Pick<ScmRepositoryList, 'more'> | undefined, filter: string): boolean => Boolean(list?.more) || Boolean(filter);

/** The toast after a provision that finished. */
export function provisionedToast(appName: string, result: Pick<BitriseSetup, 'alreadyProvisioned' | 'bitriseAppSlug'>): { title: string; description: string | undefined } {
  return { title: result.alreadyProvisioned ? `${appName} is already on Bitrise` : `${appName} is on Bitrise`, description: result.bitriseAppSlug ?? undefined };
}
